import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import device from 'managers/device';
import { NodeTracker as Tracker } from 'managers/nodes/tracker';
import { EffectsContainer } from 'mixins/withEffects';
import './viewer.html';
import './viewer.scss';

components.preload('content-box-viewer');

export interface MasterViewerVMConfig {
    content: SubscribableOrNullableValue<string>
    preview: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class MasterViewerVM {
    bodyNode = ko.observable<HTMLElement>();
    contentNode = ko.observable<HTMLElement>();

    parsedContent: Subscribable<string>
    previewContent: Subscribable<string>
    contentBodyHeight: Subscribable<string>

    preview: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: MasterViewerVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.preview = ko.flattenComputed(config.preview, false);
        const content = ko.flattenComputed(config.content, '');        

        const { rect: elementRect } = new Tracker({ node: <HTMLElement>info.element });
        const { rect: bodyRect } = new Tracker({ node: this.bodyNode });
        const { rect: contentRect } = new Tracker({ node: this.contentNode });

        const bodyHeight = bodyRect.pluck(rect => rect.height, 0);
        const contentHeight = contentRect.pluck(rect => rect.height, 0);

        const offset = elementRect.pluck(rect => {
            device.width();
            device.height();

            return rect.top - (document.body.getBoundingClientRect()?.top ?? 0);
        }, 0).extend({ deferred: true });

        const bodyShrink = ko.pureComputed(() => bodyHeight() - contentHeight() + offset()).extend({ rateLimit: { timeout: 0, method: 'notifyWhenChangesStop' } });

        this.contentBodyHeight = ko.pureComputed(() => device.height() - bodyShrink()).toPx().extend({ deferred: true });

        this.parsedContent = content.mapSingle(content => this.parseContent(content));
        this.previewContent = ko.pureComputed(this.getPreviewContent, this);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'master-viewer': true
            },

            'widgetsContainer': true
        });
    }

    private parseContent(content: string) {
        return content.replace(/{Body}/i, $('#wcc-components-master-viewer-body-content').text());
    }

    private getPreviewContent() {
        return $('#wcc-components-master-viewer-body-default-content').text();
    }
}